import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/ApiProvider';
import { ApiError } from '../api/client';
import { useStartTrial, useSubscription, useUpdateSubscription } from '../api/hooks';
import type { PlanId } from '../api/types';
import { Brand } from '../app/Brand';
import { LoginControls } from '../auth/LoginControls';
import { usePermission } from '../auth/permissions';
import { useAuthStore, useCurrentMembership, useIsAuthenticated } from '../auth/store';
import { useT, type TranslationKey } from '../i18n';
import { Button, Card, Skeleton, toast } from '../ui';
import { defaultSeats, SeatsField } from '../features/billing/SeatsField';
import { isSeatPlan } from '../features/billing/plans';
import { needsOffer, useSubscriptionGate } from './gate';
import s from './ChoosePlanPage.module.css';

/** Offres payantes proposées (le plan gratuit n'ouvre pas la console). */
const PAID_PLANS: PlanId[] = ['pro', 'team', 'enterprise'];

/**
 * Écran « Choisir une offre » : essai PRO de 15 jours (une seule fois par organisation) ou abonnement payant.
 * Affiché avant toute autre page tant que l'organisation est au plan gratuit.
 */
export function ChoosePlanPage() {
  const { t } = useT();
  const api = useApi();
  const authed = useIsAuthenticated();
  const orgId = useAuthStore((x) => x.orgId);
  const user = useAuthStore((x) => x.user);
  const membership = useCurrentMembership();
  const canManage = usePermission('billing.manage');
  const sub = useSubscription();
  const trial = useStartTrial();
  const update = useUpdateSubscription();
  const clearGate = useSubscriptionGate((g) => g.clear);
  const clearSession = useAuthStore((x) => x.clear);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [seats, setSeats] = useState<Partial<Record<PlanId, number>>>({});
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  // Une offre est déjà active (choisie ici ou ailleurs) : la console s'ouvre.
  const offerActive = !!sub.data && !needsOffer(sub.data.planId);
  useEffect(() => {
    if (offerActive) clearGate();
  }, [offerActive, clearGate]);

  if (!authed || !user) return <Navigate to="/login" replace />;
  if (!orgId) return <Navigate to="/select-org" replace />;
  if (offerActive) {
    return <Navigate to={from === '/choose-plan' ? '/' : from} replace />;
  }

  // Essai déjà consommé : la date de fin existe alors que l'abonnement n'est pas en essai.
  const trialUsed = !!sub.data?.trialEndsAt && sub.data.status !== 'trialing';
  const busy = trial.isPending || update.isPending;

  const fail = (err: unknown) => {
    const code = err instanceof ApiError ? err.code : undefined;
    toast.error(code === 'conflict' ? t('choosePlan.error.conflict') : t('choosePlan.error'), code);
    void qc.invalidateQueries({ queryKey: ['org', orgId, 'subscription'] });
  };
  const startTrial = async () => {
    try {
      await trial.mutateAsync();
      clearGate();
      toast.success(t('choosePlan.trial.done'));
    } catch (err) {
      fail(err);
    }
  };
  const subscribe = async (planId: PlanId) => {
    try {
      // Point d'extension : le paiement réel (SATIM/CIB, Stripe) s'insère ici, avant le changement de plan.
      await update.mutateAsync({ planId, ...(isSeatPlan(planId) ? { technicians: seats[planId] ?? defaultSeats(sub.data?.technicians.used ?? 1) } : {}) });
      clearGate();
      toast.success(t('choosePlan.paid.done', { plan: t(`billing.plan.${planId}` as TranslationKey) }));
    } catch (err) {
      fail(err);
    }
  };
  const logout = async () => {
    try {
      await api.logout();
    } finally {
      clearSession();
      qc.clear();
      useSubscriptionGate.getState().clear();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className={s.page}>
      <header className={s.top}>
        <Brand />
        <div className={s.controls}>
          <LoginControls />
        </div>
      </header>
      <main className={s.main}>
        <h1>{t('choosePlan.title')}</h1>
        <p className={s.lead}>{t('choosePlan.subtitle', { org: membership?.orgName ?? '' })}</p>
        {sub.isPending ? (
          <Skeleton height={160} radius={20} />
        ) : !canManage ? (
          <Card as="section" padding="none">
            <div className={s.offer}>
              <p role="status">{t('choosePlan.notAllowed')}</p>
            </div>
          </Card>
        ) : (
          <>
            <Card as="section" padding="none">
              <div className={s.offer}>
                <h2>{t('choosePlan.trial.title')}</h2>
                <p>{trialUsed ? t('choosePlan.trial.used') : t('choosePlan.trial.desc')}</p>
                <div className={s.actions}>
                  <Button variant="primary" onClick={startTrial} disabled={busy || trialUsed} loading={trial.isPending}>
                    {t('choosePlan.trial.cta')}
                  </Button>
                </div>
              </div>
            </Card>
            <div className={s.grid}>
              {PAID_PLANS.map((id) => (
                <Card as="section" padding="none" key={id}>
                  <div className={s.offer}>
                    <h2>{t(`billing.plan.${id}` as TranslationKey)}</h2>
                    <p>{t(`choosePlan.plan.${id}` as TranslationKey)}</p>
                    {isSeatPlan(id) && (
                      <SeatsField
                        value={seats[id] ?? defaultSeats(sub.data?.technicians.used ?? 1)}
                        onChange={(n) => setSeats((x) => ({ ...x, [id]: n }))}
                        disabled={busy}
                      />
                    )}
                    <div className={s.actions}>
                      <Button variant="secondary" onClick={() => subscribe(id)} disabled={busy} aria-label={t('choosePlan.paid.choose', { plan: t(`billing.plan.${id}` as TranslationKey) })}>
                        {t('choosePlan.paid.cta')}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
        <p className={s.note}>{t('choosePlan.freeNote')}</p>
        <div className={s.actions}>
          {(user.memberships.length ?? 0) > 1 && (
            <Button variant="ghost" onClick={() => navigate('/select-org', { state: { from: '/' } })}>
              {t('choosePlan.switchOrg')}
            </Button>
          )}
          <Button variant="ghost" onClick={logout}>
            {t('shell.logout')}
          </Button>
        </div>
      </main>
    </div>
  );
}
